import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldAlert,
  ShieldCheck,
  Search,
  Plus,
  Trash2,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  User,
  Briefcase,
  Filter,
  ExternalLink,
  ShieldQuestion,
  Scale,
} from "lucide-react";
import { conflictChecksApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface SearchParty {
  id: string;
  name: string;
  email: string;
  phone: string;
  type: string;
}

interface ConflictMatch {
  _id: string;
  score: number;
  matchType: string;
  matchedName: string;
  searchTerm: string;
  clientId?: string;
  clientName?: string;
  matterId?: string;
  matterTitle?: string;
  matterNumber?: string;
  relationship?: string;
  description?: string;
}

interface ConflictCheckResult {
  _id: string;
  status: string;
  matches: ConflictMatch[];
  recordsScanned?: number;
  searchTerms: Array<{ name?: string; email?: string; phone?: string; type?: string }>;
  resolvedBy?: { name: string };
  resolvedAt?: string;
  waiverReason?: string;
  notes?: string;
}

interface HistoryItem {
  _id: string;
  searchTerms: Array<{ name?: string; email?: string; phone?: string; type?: string }>;
  checkType?: string;
  status: string;
  matchCount: number;
  resolvedBy?: { name: string };
  resolvedAt?: string;
  createdAt: string;
  matches?: ConflictMatch[];
  waiverReason?: string;
  notes?: string;
}

type Tab = "search" | "history";

const PARTY_TYPES = [
  { value: "client", label: "Client" },
  { value: "opposing_party", label: "Opposing Party" },
  { value: "related_party", label: "Related Party" },
];

const STATUS_FILTERS = [
  { value: "", label: "All Statuses" },
  { value: "clear", label: "Clear" },
  { value: "potential", label: "Potential" },
  { value: "confirmed", label: "Confirmed" },
  { value: "waived", label: "Waived" },
];

/* ─── Helpers ────────────────────────────────────────────────────────── */

function newParty(): SearchParty {
  return { id: crypto.randomUUID(), name: "", email: "", phone: "", type: "client" };
}

function scoreColor(score: number) {
  if (score >= 80) return { bg: "bg-red-100", text: "text-red-700", ring: "ring-red-200" };
  if (score >= 50) return { bg: "bg-amber-100", text: "text-amber-700", ring: "ring-amber-200" };
  return { bg: "bg-slate-100", text: "text-slate-600", ring: "ring-slate-200" };
}

function matchTypeBadge(type: string) {
  switch (type) {
    case "former_client":
      return { label: "Former Client", className: "bg-red-100 text-red-700" };
    case "opposing_party":
      return { label: "Opposing Party", className: "bg-amber-100 text-amber-700" };
    case "opposing_counsel":
      return { label: "Opposing Counsel", className: "bg-orange-100 text-orange-700" };
    case "related_party":
      return { label: "Related Party", className: "bg-purple-100 text-purple-700" };
    case "current_client":
      return { label: "Current Client", className: "bg-blue-100 text-blue-700" };
    default:
      return { label: type?.replace(/_/g, " ") || "Unknown", className: "bg-slate-100 text-slate-600" };
  }
}

function statusBadge(status: string) {
  switch (status) {
    case "clear":
      return { label: "Clear", className: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 };
    case "potential":
      return { label: "Potential", className: "bg-amber-100 text-amber-700", Icon: AlertTriangle };
    case "confirmed":
      return { label: "Confirmed", className: "bg-red-100 text-red-700", Icon: XCircle };
    case "waived":
      return { label: "Waived", className: "bg-blue-100 text-blue-700", Icon: ShieldCheck };
    default:
      return { label: status, className: "bg-slate-100 text-slate-600", Icon: ShieldQuestion };
  }
}

function highlightMatch(text: string, term: string): React.ReactNode {
  if (!term || !text) return text;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-amber-200/60 font-semibold rounded px-0.5">{text.slice(idx, idx + term.length)}</span>
      {text.slice(idx + term.length)}
    </>
  );
}

/* ─── Subtle Particles (for clear result) ────────────────────────────── */

const ClearParticles: React.FC = () => {
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    x: Math.cos((i / 12) * Math.PI * 2) * 60,
    y: Math.sin((i / 12) * Math.PI * 2) * 60,
    delay: i * 0.05,
    size: 3 + Math.random() * 4,
  }));

  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            backgroundColor: p.id % 3 === 0 ? "#10B981" : p.id % 3 === 1 ? "#34D399" : "#6EE7B7",
          }}
          initial={{ opacity: 0, x: 0, y: 0, scale: 0 }}
          animate={{
            opacity: [0, 1, 0],
            x: p.x,
            y: p.y,
            scale: [0, 1.2, 0],
          }}
          transition={{
            duration: 1,
            delay: 0.3 + p.delay,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
};

/* ─── Waiver Modal ───────────────────────────────────────────────────── */

const WaiverModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  submitting: boolean;
}> = ({ open, onClose, onSubmit, submitting }) => {
  const [reason, setReason] = useState("");
  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title="Waive Conflict"
      subtitle="Provide a reason for waiving this conflict. This will be recorded in compliance records."
      icon={ShieldAlert}
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(reason)}
            disabled={!reason.trim() || submitting}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
            style={{ backgroundColor: "#D97706" }}
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </span>
            ) : (
              "Waive with Reason"
            )}
          </button>
        </div>
      }
    >
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={4}
        placeholder="Explain why this conflict is being waived (e.g., informed consent obtained from all parties, no adverse interest, etc.)..."
        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors resize-none"
      />
    </PremiumModal>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────── */

const ConflictCheckPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("search");

  // Search state
  const [parties, setParties] = useState<SearchParty[]>([newParty()]);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<ConflictCheckResult | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Resolution state
  const [resolving, setResolving] = useState(false);
  const [showWaiverModal, setShowWaiverModal] = useState(false);

  // History state
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [historyFilter, setHistoryFilter] = useState("");
  const [expandedHistory, setExpandedHistory] = useState<string | null>(null);

  const tabs: { id: Tab; label: string }[] = [
    { id: "search", label: "Search" },
    { id: "history", label: "History" },
  ];

  /* ── Party management ──────────────────────────────────────────── */

  const updateParty = (id: string, field: keyof SearchParty, value: string) => {
    setParties((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  };

  const addParty = () => {
    setParties((prev) => [...prev, newParty()]);
  };

  const removeParty = (id: string) => {
    if (parties.length <= 1) return;
    setParties((prev) => prev.filter((p) => p.id !== id));
  };

  /* ── Search ────────────────────────────────────────────────────── */

  const canSearch = parties.some((p) => p.name.trim() || p.email.trim() || p.phone.trim());

  const handleSearch = async () => {
    if (!canSearch) return;
    setSearching(true);
    setResult(null);
    setHasSearched(false);
    try {
      const searchTerms = parties.map((p) => ({
        ...(p.name.trim() ? { name: p.name.trim() } : {}),
        ...(p.email.trim() ? { email: p.email.trim() } : {}),
        ...(p.phone.trim() ? { phone: p.phone.trim() } : {}),
        type: p.type,
      }));
      const res = await conflictChecksApi.search({ searchTerms });
      setResult(res.data);
      setHasSearched(true);
    } catch (err) {
      console.error("Conflict check failed:", err);
      setHasSearched(true);
    } finally {
      setSearching(false);
    }
  };

  /* ── Resolution ────────────────────────────────────────────────── */

  const handleResolve = async (status: string, waiverReason?: string) => {
    if (!result?._id) return;
    setResolving(true);
    try {
      const data: any = { status };
      if (waiverReason) data.waiverReason = waiverReason;
      await conflictChecksApi.resolve(result._id, data);
      setResult((prev) => prev ? { ...prev, status } : prev);
      setShowWaiverModal(false);
    } catch (err) {
      console.error("Failed to resolve conflict check:", err);
    } finally {
      setResolving(false);
    }
  };

  /* ── History ───────────────────────────────────────────────────── */

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params: any = { page: historyPagination.page, limit: historyPagination.limit };
      if (historyFilter) params.status = historyFilter;
      const res = await conflictChecksApi.list(params);
      const data = res.data;
      setHistory(data.data || []);
      if (data.pagination) {
        setHistoryPagination((prev) => ({ ...prev, ...data.pagination }));
      }
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPagination.page, historyPagination.limit, historyFilter]);

  useEffect(() => {
    if (activeTab === "history") fetchHistory();
  }, [activeTab, fetchHistory]);

  /* ── Render ────────────────────────────────────────────────────── */

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="h-11 w-11 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#0F5132" }}
          >
            <Scale className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Conflict of Interest Check</h1>
            <p className="text-sm text-slate-500">
              Search for potential conflicts before taking on new clients or matters.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <div className="flex gap-1">
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-5 py-3 text-sm font-medium transition-colors ${
                  active ? "text-[#0F5132]" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab.label}
                {active && (
                  <motion.div
                    layoutId="conflictTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ backgroundColor: "#198754" }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === "search" ? (
          <motion.div
            key="search"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {/* Search Parties */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
              <h2 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Search className="h-4 w-4" style={{ color: "#198754" }} />
                Search Parties
              </h2>

              <div className="space-y-4">
                {parties.map((party, idx) => (
                  <motion.div
                    key={party.id}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="p-4 bg-slate-50 rounded-xl border border-slate-100"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        Party {idx + 1}
                      </span>
                      <div className="flex items-center gap-2">
                        <select
                          value={party.type}
                          onChange={(e) => updateParty(party.id, "type", e.target.value)}
                          className="text-xs rounded-lg border border-slate-200 px-2 py-1.5 bg-white focus:outline-none focus:border-emerald-500 transition-colors"
                        >
                          {PARTY_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                        {parties.length > 1 && (
                          <button
                            onClick={() => removeParty(party.id)}
                            className="p-1 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                        <input
                          type="text"
                          value={party.name}
                          onChange={(e) => updateParty(party.id, "name", e.target.value)}
                          placeholder="John Smith"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                        <input
                          type="email"
                          value={party.email}
                          onChange={(e) => updateParty(party.id, "email", e.target.value)}
                          placeholder="john@email.com"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
                        <input
                          type="tel"
                          value={party.phone}
                          onChange={(e) => updateParty(party.id, "phone", e.target.value)}
                          placeholder="+1 416 555 1234"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="flex items-center justify-between mt-4">
                <button
                  onClick={addParty}
                  className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
                  style={{ color: "#0F5132" }}
                >
                  <Plus className="h-4 w-4" />
                  Add Another Party
                </button>

                <button
                  onClick={handleSearch}
                  disabled={!canSearch || searching}
                  className="inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl disabled:opacity-50 transition-colors"
                  style={{ backgroundColor: "#0F5132" }}
                  onMouseEnter={(e) => { if (!searching && canSearch) e.currentTarget.style.backgroundColor = "#146C43"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#0F5132"; }}
                >
                  {searching ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                      >
                        <ShieldAlert className="h-5 w-5" />
                      </motion.div>
                      Scanning records...
                    </>
                  ) : (
                    <>
                      <Search className="h-5 w-5" />
                      Run Conflict Check
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Results */}
            <AnimatePresence>
              {hasSearched && result && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {result.status === "clear" || (result.matches && result.matches.length === 0) ? (
                    /* ── Clear result ── */
                    <motion.div
                      className="bg-white rounded-xl shadow-sm border border-emerald-200 p-8 text-center relative overflow-hidden"
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
                    >
                      <ClearParticles />
                      <motion.div
                        className="relative z-10"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 400, damping: 15, delay: 0.2 }}
                      >
                        <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                          <CheckCircle2 className="h-9 w-9 text-emerald-600" />
                        </div>
                      </motion.div>
                      <h3 className="text-xl font-bold text-emerald-800 mb-1">No Conflicts Found</h3>
                      <p className="text-sm text-slate-500 mb-4">
                        {result.recordsScanned
                          ? `Scanned ${result.recordsScanned.toLocaleString()} records across clients, matters, and opposing parties.`
                          : "All records have been checked for potential conflicts."}
                      </p>
                      <p className="text-xs text-slate-400">
                        This check has been recorded for your compliance records.
                      </p>
                    </motion.div>
                  ) : (
                    /* ── Potential conflicts ── */
                    <div className="space-y-4">
                      <motion.div
                        className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                      >
                        <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-amber-800">
                            {result.matches.length} Potential Conflict{result.matches.length !== 1 ? "s" : ""} Found
                          </h3>
                          <p className="text-sm text-amber-700">
                            Review each match below and take appropriate action.
                          </p>
                        </div>
                      </motion.div>

                      {/* Match cards */}
                      {result.matches.map((match, idx) => {
                        const sc = scoreColor(match.score);
                        const mtb = matchTypeBadge(match.matchType);
                        return (
                          <motion.div
                            key={match._id || idx}
                            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: 0.2 + idx * 0.1 }}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <motion.span
                                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold ring-1 ${sc.bg} ${sc.text} ${sc.ring}`}
                                  initial={{ scale: 0.5 }}
                                  animate={{ scale: [0.5, 1.15, 1] }}
                                  transition={{ duration: 0.5, delay: 0.4 + idx * 0.1 }}
                                >
                                  {match.score}% match
                                </motion.span>
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${mtb.className}`}>
                                  {mtb.label}
                                </span>
                              </div>
                            </div>

                            <p className="text-sm text-slate-700 mb-2">
                              &ldquo;{match.searchTerm}&rdquo; matches{" "}
                              <span className="font-semibold">
                                {highlightMatch(match.matchedName, match.searchTerm)}
                              </span>
                            </p>

                            {(match.matterNumber || match.matterTitle) && (
                              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                                <Briefcase className="h-3.5 w-3.5" />
                                <span>
                                  {match.matterNumber && <span className="font-medium">{match.matterNumber}</span>}
                                  {match.matterNumber && match.matterTitle && " — "}
                                  {match.matterTitle}
                                </span>
                              </div>
                            )}

                            {match.clientName && (
                              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                                <User className="h-3.5 w-3.5" />
                                <span>{match.clientName}</span>
                              </div>
                            )}

                            {match.relationship && (
                              <p className="text-sm text-slate-500 italic mt-1">
                                {match.relationship}
                              </p>
                            )}

                            {match.description && (
                              <p className="text-sm text-slate-500 mt-1">
                                {match.description}
                              </p>
                            )}

                            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                              {match.matterId && (
                                <button
                                  onClick={() => navigate(`/matters/${match.matterId}`)}
                                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  View Matter
                                </button>
                              )}
                              {match.clientId && (
                                <button
                                  onClick={() => navigate(`/clients/${match.clientId}`)}
                                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  View Client
                                </button>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}

                      {/* Resolution actions */}
                      {result.status !== "clear" && result.status !== "confirmed" && result.status !== "waived" && (
                        <motion.div
                          className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.3 + result.matches.length * 0.1 }}
                        >
                          <h4 className="text-sm font-semibold text-slate-700 mb-3">Resolution</h4>
                          <div className="flex flex-wrap gap-3">
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleResolve("clear")}
                              disabled={resolving}
                              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
                              style={{ backgroundColor: "#198754" }}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              Approve — No Conflict
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleResolve("confirmed")}
                              disabled={resolving}
                              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                            >
                              <XCircle className="h-4 w-4" />
                              Block — Conflict Confirmed
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => setShowWaiverModal(true)}
                              disabled={resolving}
                              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold bg-amber-600 hover:bg-amber-700 disabled:opacity-50 transition-colors"
                            >
                              <ShieldAlert className="h-4 w-4" />
                              Waive with Reason
                            </motion.button>
                          </div>
                        </motion.div>
                      )}

                      {/* Already resolved banner */}
                      {(result.status === "clear" || result.status === "confirmed" || result.status === "waived") && (
                        <motion.div
                          className={`rounded-xl p-4 flex items-center gap-3 ${
                            result.status === "clear" ? "bg-emerald-50 border border-emerald-200" :
                            result.status === "waived" ? "bg-blue-50 border border-blue-200" :
                            "bg-red-50 border border-red-200"
                          }`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.3 }}
                        >
                          {result.status === "clear" && <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />}
                          {result.status === "confirmed" && <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />}
                          {result.status === "waived" && <ShieldCheck className="h-5 w-5 text-blue-600 flex-shrink-0" />}
                          <div>
                            <p className={`text-sm font-semibold ${
                              result.status === "clear" ? "text-emerald-800" :
                              result.status === "waived" ? "text-blue-800" :
                              "text-red-800"
                            }`}>
                              {result.status === "clear" && "Approved — No Conflict"}
                              {result.status === "confirmed" && "Blocked — Conflict Confirmed"}
                              {result.status === "waived" && "Conflict Waived"}
                            </p>
                            {result.resolvedBy && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                by {result.resolvedBy.name}
                                {result.resolvedAt && ` on ${new Date(result.resolvedAt).toLocaleDateString()}`}
                              </p>
                            )}
                            {result.waiverReason && (
                              <p className="text-xs text-slate-500 mt-1 italic">Reason: {result.waiverReason}</p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Loading placeholder */}
            {searching && (
              <motion.div
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 flex flex-col items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                >
                  <ShieldAlert className="h-10 w-10 text-emerald-600 mb-4" />
                </motion.div>
                <p className="text-lg font-semibold text-slate-700 mb-1">Scanning records...</p>
                <p className="text-sm text-slate-500">
                  Checking clients, matters, opposing parties, and related contacts.
                </p>
              </motion.div>
            )}
          </motion.div>
        ) : (
          /* ── History Tab ── */
          <motion.div
            key="history"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {/* Filter */}
            <div className="flex items-center gap-3 mb-4">
              <Filter className="h-4 w-4 text-slate-400" />
              <select
                value={historyFilter}
                onChange={(e) => {
                  setHistoryFilter(e.target.value);
                  setHistoryPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 transition-colors"
              >
                {STATUS_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>

            {historyLoading ? (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
              </div>
            ) : history.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 flex flex-col items-center justify-center text-center">
                <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <ShieldQuestion className="h-7 w-7 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-1">No conflict checks yet</h3>
                <p className="text-sm text-slate-500">
                  Run your first conflict check to see history here.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((item, idx) => {
                  const sb = statusBadge(item.status);
                  const StatusIcon = sb.Icon;
                  const isExpanded = expandedHistory === item._id;

                  return (
                    <motion.div
                      key={item._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedHistory(isExpanded ? null : item._id)}
                        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-xs text-slate-500 flex-shrink-0">
                            <Clock className="h-3.5 w-3.5" />
                            {new Date(item.createdAt).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {item.searchTerms
                                ?.map((t) => t.name || t.email || t.phone)
                                .filter(Boolean)
                                .join(", ") || "Unnamed search"}
                            </p>
                          </div>

                          <div className="flex items-center gap-3 flex-shrink-0">
                            {item.checkType && (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                {item.checkType}
                              </span>
                            )}
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sb.className}`}>
                              <StatusIcon className="h-3 w-3" />
                              {sb.label}
                            </span>
                            <span className="text-xs text-slate-400">
                              {item.matchCount} match{item.matchCount !== 1 ? "es" : ""}
                            </span>
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-slate-400 ml-2 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-400 ml-2 flex-shrink-0" />
                        )}
                      </button>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-4 border-t border-slate-100 pt-4">
                              {/* Search terms */}
                              <div className="mb-4">
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                                  Search Terms
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {item.searchTerms?.map((t, i) => (
                                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 rounded-lg text-xs text-slate-600">
                                      {t.name && <><User className="h-3 w-3" /> {t.name}</>}
                                      {t.email && <span className="text-slate-400">{t.email}</span>}
                                      {t.phone && <span className="text-slate-400">{t.phone}</span>}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* Matches */}
                              {item.matches && item.matches.length > 0 && (
                                <div className="mb-4">
                                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                                    Matches
                                  </p>
                                  <div className="space-y-2">
                                    {item.matches.map((m, mi) => {
                                      const sc = scoreColor(m.score);
                                      const mtb = matchTypeBadge(m.matchType);
                                      return (
                                        <div key={mi} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${sc.bg} ${sc.text}`}>
                                            {m.score}%
                                          </span>
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${mtb.className}`}>
                                            {mtb.label}
                                          </span>
                                          <span className="text-sm text-slate-700">{m.matchedName}</span>
                                          {m.matterTitle && (
                                            <span className="text-xs text-slate-400 truncate">{m.matterTitle}</span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Resolution info */}
                              {item.resolvedBy && (
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <span>Resolved by {item.resolvedBy.name}</span>
                                  {item.resolvedAt && (
                                    <span>on {new Date(item.resolvedAt).toLocaleDateString()}</span>
                                  )}
                                </div>
                              )}

                              {item.waiverReason && (
                                <p className="text-xs text-slate-500 italic mt-1">
                                  Waiver reason: {item.waiverReason}
                                </p>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}

                {/* Pagination */}
                {historyPagination.pages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-4">
                    <button
                      onClick={() => setHistoryPagination((p) => ({ ...p, page: p.page - 1 }))}
                      disabled={historyPagination.page <= 1}
                      className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-slate-500">
                      Page {historyPagination.page} of {historyPagination.pages}
                    </span>
                    <button
                      onClick={() => setHistoryPagination((p) => ({ ...p, page: p.page + 1 }))}
                      disabled={historyPagination.page >= historyPagination.pages}
                      className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Waiver modal */}
      <WaiverModal
        open={showWaiverModal}
        onClose={() => setShowWaiverModal(false)}
        onSubmit={(reason) => handleResolve("waived", reason)}
        submitting={resolving}
      />
    </div>
  );
};

export default ConflictCheckPage;
